import { useEffect, useRef, useState, useCallback } from 'react';
import { getSupabaseClient } from '../data/supabase';

type HealthState = 'checking' | 'ok' | 'degraded' | 'down';

const POLL_INTERVAL = 60_000; // 60 seconds
const TIMEOUT_MS = 8_000;
const DEGRADED_THRESHOLD_MS = 3_000;

interface SupabaseStatusProps {
  compact?: boolean;
}

export default function SupabaseStatus({ compact = false }: SupabaseStatusProps = {}) {
  const [health, setHealth] = useState<HealthState>('checking');
  const [latency, setLatency] = useState<number | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const timerRef = useRef<number>(0);

  const check = useCallback(async () => {
    const start = performance.now();
    try {
      const client = getSupabaseClient();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

      // Lightweight query: just ask for 1 row from a small table.
      // Even if RLS denies access the HTTP round-trip still proves connectivity.
      const { error } = await client
        .from('app_settings')
        .select('id')
        .limit(1)
        .abortSignal(controller.signal);

      clearTimeout(timeout);
      const elapsed = Math.round(performance.now() - start);
      setLatency(elapsed);
      setLastChecked(new Date());

      if (error) {
        const msg = error.message || '';
        // Network-level failures (Cloudflare 522, fetch failures, etc.)
        if (
          msg.includes('Failed to fetch') ||
          msg.includes('NetworkError') ||
          msg.includes('timed out') ||
          msg.includes('522') ||
          msg.includes('503') ||
          msg.includes('502')
        ) {
          setHealth('down');
        } else {
          // RLS denied, permission error, etc. - connection is fine
          setHealth(elapsed > DEGRADED_THRESHOLD_MS ? 'degraded' : 'ok');
        }
      } else {
        setHealth(elapsed > DEGRADED_THRESHOLD_MS ? 'degraded' : 'ok');
      }
    } catch (err: unknown) {
      const elapsed = Math.round(performance.now() - start);
      setLatency(elapsed);
      setLastChecked(new Date());

      if (err instanceof DOMException && err.name === 'AbortError') {
        setHealth('degraded');
      } else {
        setHealth('down');
      }
    }
  }, []);

  useEffect(() => {
    check();
    timerRef.current = window.setInterval(check, POLL_INTERVAL);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [check]);

  const statusLabel: Record<HealthState, string> = {
    checking: 'checking',
    ok: 'online',
    degraded: 'slow',
    down: 'offline',
  };

  const statusHelp: Record<HealthState, string> = {
    checking: 'Checking the database connection.',
    ok: 'Database connection is available.',
    degraded: 'Database connection is responding slowly. Click to recheck.',
    down: 'Database connection is offline. Check the connection and try again.',
  };

  const tooltipLines = [
    `Database: ${statusLabel[health]}`,
    statusHelp[health],
    health !== 'down' && latency != null ? `Response time: ${latency}ms` : null,
    lastChecked ? `Last check: ${lastChecked.toLocaleTimeString()}` : null,
  ].filter(Boolean);

  return (
    <button
      type="button"
      className={`supabase-status-bug text-xxs${compact ? ' supabase-status-bug--compact' : ''}`}
      data-health={health}
      title={tooltipLines.join('\n')}
      onClick={check}
      aria-label={`${statusHelp[health]} Click to recheck.`}
    >
      <span
        className={`supabase-status-bug__dot${health === 'checking' ? ' supabase-status-bug__dot--pulse' : ''}`}
        aria-hidden="true"
      />
      <span className="supabase-status-bug__label">
        {compact ? 'DB' : 'Database'}
      </span>
      {!compact ? <span className="supabase-status-bug__state">{statusLabel[health]}</span> : null}
    </button>
  );
}
